import { useEffect, useState } from "react";
import { Ban, Copy, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtDate } from "@/lib/api";

const status = (d) => {
  if (d.revoked) return ["Revoked", "bg-[#F5EFE6] text-[#5C5255]"];
  if (d.expires_at < new Date().toISOString()) return ["Expired", "bg-[#F5EFE6] text-[#5C5255]"];
  if (d.email_status === "sent") return ["Sent", "bg-[#E8EFE9] text-[#2A4731]"];
  return ["Email failed", "bg-[#FBEDE0] text-[#945B17]"];
};

export default function Deliveries() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/deliveries").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const resend = async (d) => {
    try {
      const r = await api.post(`/deliveries/${d.id}/resend`);
      r.data.email_status === "sent" ? toast.success("Re-sent") : toast.error(r.data.email_error);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const revoke = async (d) => {
    await api.post(`/deliveries/${d.id}/revoke`);
    toast.success("Link revoked");
    load();
  };

  return (
    <div>
      <PageHeader eyebrow="Every copy sent from the press" title="Deliveries" />
      <div className="px-6 md:px-12 py-8">
        <div className="bg-[#FFFDF9] border border-[#2A2325]/10 overflow-x-auto">
          <Table data-testid="deliveries-table">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Downloads</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => {
                const [label, cls] = status(d);
                return (
                  <TableRow key={d.id} data-testid={`delivery-row-${d.id}`}>
                    <TableCell className="font-body-serif text-base">{d.product_title}{d.order_ref && <div className="text-xs text-[#5C5255] font-sans">Order {d.order_ref}</div>}</TableCell>
                    <TableCell>{d.customer_name && <div>{d.customer_name}</div>}<div className="text-[#5C5255] text-xs">{d.customer_email}</div></TableCell>
                    <TableCell className="text-sm">{fmtDate(d.created_at)}<div className="text-xs text-[#5C5255]">expires {fmtDate(d.expires_at)}</div></TableCell>
                    <TableCell className="font-mono-meta" data-testid={`delivery-downloads-${d.id}`}>{d.download_count}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 ${cls}`} title={d.email_error || ""} data-testid={`delivery-status-${d.id}`}>{label}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button onClick={() => { navigator.clipboard.writeText(d.link); toast.success("Link copied"); }} className="p-2 text-[#5C5255] hover:text-[#5E2B38]" title="Copy link" data-testid={`delivery-copy-${d.id}`}><Copy size={15} /></button>
                      <button onClick={() => resend(d)} className="p-2 text-[#5C5255] hover:text-[#5E2B38]" title="Re-send email (renews 30 days)" data-testid={`delivery-resend-${d.id}`}><RotateCw size={15} /></button>
                      {!d.revoked && <button onClick={() => revoke(d)} className="p-2 text-[#5C5255] hover:text-[#9B2C2C]" title="Revoke link" data-testid={`delivery-revoke-${d.id}`}><Ban size={15} /></button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!items.length && <p className="p-8 font-body-serif italic text-[#5C5255]" data-testid="deliveries-empty">Nothing sent yet.</p>}
        </div>
      </div>
    </div>
  );
}
